import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Pagination } from './ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { 
  Warehouse, 
  ArrowLeft, 
  Loader2, 
  Download,
  RefreshCw,
  Search,
  X,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { inventoryReportApiService, HeatNumberConsumptionReportDto } from '../services/inventoryReportApi';
import { itemCategoryApiService, ItemCategory } from '../services/itemCategoryApi';
import { useBranch } from '../contexts/BranchContext';
import { getRMInventoryReportConfig, getPaginationConfig } from '../config/uiConfig';

const RMInventoryReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<HeatNumberConsumptionReportDto[]>([]);
  
  // Filter and search state - initialize from URL params if present
  const [searchTerm, setSearchTerm] = useState(searchParams.get('item') || searchParams.get('heatNo') || searchParams.get('grnNumber') || '');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [tcDocUploadedFilter, setTcDocUploadedFilter] = useState<'all' | 'uploaded' | 'not_uploaded'>('all');
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [defaultCategorySet, setDefaultCategorySet] = useState(false);
  const [targetDate, setTargetDate] = useState<string>('');
  
  // Pagination state - use config default
  const paginationConfig = getPaginationConfig();
  const rmInventoryConfig = getRMInventoryReportConfig();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(paginationConfig.defaultItemsPerPage);
  
  // Last month data state (for comparison)
  const [lastMonthData, setLastMonthData] = useState<{
    inventoryValue: number;
    inventoryCost: number;
  } | null>(null);
  const [loadingLastMonth, setLoadingLastMonth] = useState(false);
  

  const loadReport = async () => {
    if (!selectedBranch) {
      toast.error('Please select a branch to view the report');
      return;
    }

    try {
      setLoading(true);
      
      let response;
      if (targetDate) {
        // Use as-of-date API if date is selected
        const selectedCategory = categories.find(cat => cat.id.toString() === selectedCategoryId);
        const categoryName = selectedCategory?.categoryName;
        response = await inventoryReportApiService.getInventoryAsOfDate(
          targetDate,
          selectedBranch,
          categoryName
        );
      } else {
        // Use regular API if no date is selected
        response = await inventoryReportApiService.getHeatNumberConsumptionReportByBranch(selectedBranch);
      }
      
      if (response.success && response.data) {
        // if remaining quantity is less than 0, set it to 0
        const data = response.data.map((item: HeatNumberConsumptionReportDto) => ({
          ...item,
          remainingQuantity: item.remainingQuantity! < 0 ? 0 : item.remainingQuantity
        }));
        setReportData(data);
        toast.success(targetDate ? `Report loaded for ${targetDate}` : 'Report loaded successfully');
      } else {
        toast.error(response.message || 'Failed to load report');
        setReportData([]);
      }
    } catch (error) {
      // console.error('Error loading report:', error);
      toast.error('Failed to load report');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate last month's inventory data using GRN dates
  // This function now uses filteredData to respect category and search filters
  const calculateLastMonthInventory = (dataToUse: HeatNumberConsumptionReportDto[]) => {
    if (!dataToUse || dataToUse.length === 0) {
      setLastMonthData(null);
      return;
    }

    try {
      setLoadingLastMonth(true);
      
      // Get the last day of last month (end of day)
      const now = new Date();
      const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDayOfLastMonth = new Date(firstDayOfCurrentMonth);
      lastDayOfLastMonth.setDate(0); // Get last day of previous month
      lastDayOfLastMonth.setHours(23, 59, 59, 999); // End of day
      
      // Filter items where GRN date is on or before the last day of last month
      // Use the filtered data (which already respects category and search filters)
      const lastMonthItems = dataToUse.filter((item) => {
        if (!item.grnDate) return false;
        
        try {
          const grnDate = new Date(item.grnDate);
          // Reset time to start of day for accurate comparison
          grnDate.setHours(0, 0, 0, 0);
          const lastDay = new Date(lastDayOfLastMonth);
          lastDay.setHours(0, 0, 0, 0);
          
          return grnDate <= lastDay;
        } catch (error) {
          // console.error('Error parsing GRN date:', item.grnDate, error);
          return false;
        }
      });
      
      // Calculate inventory value and cost for last month
      // For items received up to last month end, we calculate:
      // - Inventory value: sum of remaining quantities (what's left from items received by end of last month)
      // - Inventory cost: sum of (remaining quantity × unit price)
      const lastMonthInventoryValue = lastMonthItems.reduce((sum, item) => {
        return sum + (item.remainingQuantity || 0);
      }, 0);
      
      const lastMonthInventoryCost = lastMonthItems.reduce((sum, item) => {
        const remainingQty = item.remainingQuantity || 0;
        const unitPrice = item.unitPrice || 0;
        return sum + (remainingQty * unitPrice);
      }, 0);
      
      setLastMonthData({
        inventoryValue: lastMonthInventoryValue,
        inventoryCost: lastMonthInventoryCost
      });
    } catch (error) {
      // console.error('Error calculating last month inventory:', error);
      setLastMonthData(null);
    } finally {
      setLoadingLastMonth(false);
    }
  };

  // Load categories and set default category from config
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const categoriesData = await itemCategoryApiService.getItemCategories();
        setCategories(categoriesData || []);
        
        // Set default category from config after categories are loaded (only if not already set)
        if (!defaultCategorySet && categoriesData && categoriesData.length > 0 && rmInventoryConfig.defaultCategory) {
          const defaultCategoryName = rmInventoryConfig.defaultCategory.trim();
          const defaultCategory = categoriesData.find(
            (cat) => cat.categoryName?.trim().toLowerCase() === defaultCategoryName.toLowerCase()
          );
          if (defaultCategory && defaultCategory.id) {
            setSelectedCategoryId(defaultCategory.id.toString());
            setDefaultCategorySet(true);
            // console.log(`Default category set to: ${defaultCategory.categoryName} (ID: ${defaultCategory.id})`);
          } else {
            console.warn(`Default category "${rmInventoryConfig.defaultCategory}" not found in categories list. Available categories:`, 
              categoriesData.map(c => c.categoryName).join(', '));
          }
        }
      } catch (error) {
        // console.error('Error loading categories:', error);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    if (selectedBranch !== null) {
      loadReport();
    }
  }, [selectedBranch]);

  const handleExport = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Define CSV headers
      const headers = [
        'Heat No',
        'Item SKU',
        'Item Name',
        'Grade',
        'Supplier',
        'PO Number',
        'GRN Number',
        'GRN Date',
        'Supplier Heat No',
        'TC No',
        'TC Doc Uploaded',
        'DC No',
        'Unit Price',
        'PO Qty',
        'Received Qty',
        'NOP',
        'Total Consumed',
        'Challan Inbound',
        'Challan Outbound',
        'Reject Qty',
        'Remaining Qty',
        'Remaining NOP'
      ];

      // Convert data to CSV rows
      const csvRows = [
        headers.join(','),
        ...filteredData.map((item) => {
          const row = [
            `"${item.heatNo || ''}"`,
            `"${item.rawItemSku || ''}"`,
            `"${(item.itemName || '').replace(/"/g, '""')}"`, // Escape quotes in item name
            `"${item.grade || ''}"`,
            `"${(item.supplierName || '').replace(/"/g, '""')}"`, // Escape quotes in supplier name
            `"${item.poNumber || ''}"`,
            `"${item.grnNumber || ''}"`,
            `"${item.grnDate ? (() => {
              const date = new Date(item.grnDate);
              const day = String(date.getDate()).padStart(2, '0');
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const year = date.getFullYear();
              return `${day}-${month}-${year}`;
            })() : ''}"`,
            `"${item.supplierHeatNo || ''}"`,
            `"${item.tcNo || ''}"`,
            `"${item.tcDocumentUploaded === true ? 'Yes' : 'No'}"`,
            `"${item.dcNo || ''}"`,
            item.unitPrice?.toFixed(2) || '0.00',
            item.poQuantity?.toFixed(2) || '0.00',
            item.receivedQuantity?.toFixed(2) || '0.00',
            item.nop?.toFixed(2) || '0.00',
            item.totalConsumed?.toFixed(2) || '0.00',
            item.totalChallanInbound?.toFixed(2) || '0.00',
            item.totalChallanOutbound?.toFixed(2) || '0.00',
            item.rejectQuantity?.toFixed(2) || '0.00',
            item.remainingQuantity?.toFixed(2) || '0.00',
            item.remainingNop?.toFixed(2) || '0.00'
          ];
          return row.join(',');
        })
      ];

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const branchSuffix = selectedBranch ? `_Branch-${selectedBranch}` : '';
      const filename = `RM_Inventory_Report${branchSuffix}_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${filteredData.length} records to CSV`);
    } catch (error) {
      // console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const formatNumber = (value: number | undefined | null): string => {
    if (value === null || value === undefined) return 'N/A';
    return value.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  // Format currency in Indian format (Crore, Lakh, Thousand)
  const formatCurrency = (value: number | undefined | null): string => {
    if (value === null || value === undefined || isNaN(value)) return '₹ 0';
    
    const absValue = Math.abs(value);
    let formatted = '';
    
    if (absValue >= 10000000) {
      // Crore
      const crores = absValue / 10000000;
      formatted = `₹ ${crores.toFixed(2)} Cr`;
    } else if (absValue >= 100000) {
      // Lakh
      const lakhs = absValue / 100000;
      formatted = `₹ ${lakhs.toFixed(2)} L`;
    } else if (absValue >= 1000) {
      // Thousand
      const thousands = absValue / 1000;
      formatted = `₹ ${thousands.toFixed(2)} K`;
    } else {
      formatted = `₹ ${absValue.toFixed(2)}`;
    }
    
    return formatted;
  };

  // Filter data based on search term and category
  const filteredData = useMemo(() => {
    return reportData.filter((item) => {
      // Category filter
      if (selectedCategoryId && item.rawItemCategoryId?.toString() !== selectedCategoryId) {
        return false;
      }

      // TC document uploaded filter
      if (tcDocUploadedFilter === 'uploaded' && item.tcDocumentUploaded !== true) {
        return false;
      }
      if (tcDocUploadedFilter === 'not_uploaded' && item.tcDocumentUploaded === true) {
        return false;
      }

      // If searchTerm is set (user is actively searching), use it to search across all fields
      // Otherwise, check URL params for backward compatibility
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase().trim();
        const matchesHeatNo = item.heatNo?.toLowerCase().includes(searchLower) || false;
        const matchesItemName = item.itemName?.toLowerCase().includes(searchLower) || false;
        const matchesSupplier = item.supplierName?.toLowerCase().includes(searchLower) || false;
        const matchesGrnNumber = item.grnNumber?.toLowerCase().includes(searchLower) || false;
        const matchesPoNumber = item.poNumber?.toLowerCase().includes(searchLower) || false;
        const matchesSupplierHeatNo = item.supplierHeatNo?.toLowerCase().includes(searchLower) || false;
        
        if (!matchesHeatNo && !matchesItemName && !matchesSupplier && !matchesGrnNumber && !matchesPoNumber && !matchesSupplierHeatNo) {
          return false;
        }
      } else {
        // Fallback to URL params if searchTerm is empty (for initial load from links)
        const itemParam = searchParams.get('item');
        if (itemParam && itemParam.trim()) {
          const itemLower = itemParam.toLowerCase().trim();
          if (!item.itemName?.toLowerCase().includes(itemLower)) {
            return false;
          }
        }

        // Filter by heat number from URL params
        const heatNoParam = searchParams.get('heatNo');
        if (heatNoParam && heatNoParam.trim()) {
          const heatNoLower = heatNoParam.toLowerCase().trim();
          if (!item.heatNo?.toLowerCase().includes(heatNoLower)) {
            return false;
          }
        }

        // Filter by GRN number from URL params
        const grnNumberParam = searchParams.get('grnNumber');
        if (grnNumberParam && grnNumberParam.trim()) {
          const grnLower = grnNumberParam.toLowerCase().trim();
          if (!item.grnNumber?.toLowerCase().includes(grnLower)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [reportData, selectedCategoryId, tcDocUploadedFilter, searchTerm, searchParams]);

  // Calculate last month inventory when filtered data changes (includes filter changes)
  useEffect(() => {
    if (filteredData.length > 0) {
      calculateLastMonthInventory(filteredData);
    } else {
      setLastMonthData(null);
    }
  }, [filteredData]);

  // Calculate pagination on filtered data
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Group data by category for summary table - COMMENTED OUT
  // const groupedByCategory = filteredData.reduce((acc, item) => {
  //   const categoryId = item.rawItemCategoryId || 0;
  //   const categoryName = categories.find(cat => cat.id === categoryId)?.categoryName || 'Uncategorized';
  //   
  //   if (!acc[categoryId]) {
  //     acc[categoryId] = {
  //       categoryId,
  //       categoryName,
  //       items: []
  //     };
  //   }
  //   acc[categoryId].items.push(item);
  //   return acc;
  // }, {} as Record<number, { categoryId: number; categoryName: string; items: HeatNumberConsumptionReportDto[] }>);

  // // Convert grouped data to array and sort by category name
  // const categoryGroups = Object.values(groupedByCategory).sort((a, b) => 
  //   a.categoryName.localeCompare(b.categoryName)
  // );

  // // Calculate totals for each category
  // const categoryGroupsWithTotals = categoryGroups.map(group => ({
  //   ...group,
  //   totals: {
  //     poQuantity: group.items.reduce((sum, item) => sum + (item.poQuantity || 0), 0),
  //     receivedQuantity: group.items.reduce((sum, item) => sum + (item.receivedQuantity || 0), 0),
  //     totalConsumed: group.items.reduce((sum, item) => sum + (item.totalConsumed || 0), 0),
  //     totalChallanInbound: group.items.reduce((sum, item) => sum + (item.totalChallanInbound || 0), 0),
  //     totalChallanOutbound: group.items.reduce((sum, item) => sum + (item.totalChallanOutbound || 0), 0),
  //     rejectQuantity: group.items.reduce((sum, item) => sum + (item.rejectQuantity || 0), 0),
  //     remainingQuantity: group.items.reduce((sum, item) => sum + (item.remainingQuantity || 0), 0)
  //   }
  // }));

  // // Filter to show only Raw Material and BOP categories
  // const filteredCategoryGroups = categoryGroupsWithTotals.filter(group => {
  //   const categoryName = group.categoryName.toLowerCase();
  //   return categoryName === 'raw material' || categoryName === 'bop';
  // });

  // Update URL when search term changes
  useEffect(() => {
    const currentItemParam = searchParams.get('item');
    const trimmedSearchTerm = searchTerm.trim();
    
    // Only update if the value actually changed
    if (currentItemParam !== trimmedSearchTerm) {
      const newSearchParams = new URLSearchParams(searchParams);
      if (trimmedSearchTerm) {
        newSearchParams.set('item', trimmedSearchTerm);
      } else {
        newSearchParams.delete('item');
        newSearchParams.delete('heatNo');
        newSearchParams.delete('grnNumber');
      }
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchTerm, searchParams, setSearchParams]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategoryId, tcDocUploadedFilter, reportData.length]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <Warehouse className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Material Inventory Report</CardTitle>
                  {/* <CardDescription className="text-primary-100">
                    Heat Number Consumption Report
                    {selectedBranch && ` - Branch ${selectedBranch}`}
                  </CardDescription> */}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/reports')}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Reports
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadReport}
                  disabled={loading || !selectedBranch}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={reportData.length === 0}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Report Content */}
        {!selectedBranch ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Warehouse className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600">Please select a branch to view the report</p>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600 mr-3" />
            <span className="text-gray-600">Loading report data...</span>
          </div>
        ) : reportData.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Warehouse className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600">No data available for this report</p>
              <p className="text-sm text-gray-500 mt-2">
                Try selecting a different branch or check back later
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Filters and Search - Always show when data is loaded */}
            <Card className="border-primary-200 bg-white shadow-lg">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Search Input */}
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by heat no, item name, supplier, or GRN number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-10"
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Date Picker */}
                  <div className="w-full sm:w-48">
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        type="date"
                        value={targetDate}
                        onChange={(e) => {
                          setTargetDate(e.target.value);
                        }}
                        placeholder="Select date"
                        className="pl-10"
                        max={new Date().toISOString().split('T')[0]}
                        title="Select date to view inventory as of that date"
                      />
                      {targetDate && (
                        <button
                          onClick={() => {
                            setTargetDate('');
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          title="Clear date filter"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {targetDate && (
                      <p className="text-xs text-gray-500 mt-1">Click Refresh to load inventory as of {targetDate}</p>
                    )}
                  </div>

                  {/* Refresh Button */}
                  <div className="w-full sm:w-auto">
                    <Button
                      variant="outline"
                      onClick={loadReport}
                      disabled={loading || !selectedBranch}
                      className="w-full sm:w-auto border-primary-200 text-primary-700 hover:bg-primary-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Refresh
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Category Filter */}
                  <div className="w-full sm:w-64">
                    <Select 
                      value={selectedCategoryId ? selectedCategoryId : "all"} 
                      onValueChange={(value) => setSelectedCategoryId(value === "all" ? "" : value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {loadingCategories ? (
                          <SelectItem value="loading" disabled>Loading categories...</SelectItem>
                        ) : categories.length === 0 ? (
                          <SelectItem value="no-categories" disabled>No categories found</SelectItem>
                        ) : (
                          categories
                            .filter(cat => cat.isActive)
                            .map((category) => (
                              <SelectItem key={category.id} value={category.id.toString()}>
                                {category.categoryName}
                              </SelectItem>
                            ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* TC Document Uploaded Filter */}
                  <div className="w-full sm:w-56">
                    <Select
                      value={tcDocUploadedFilter}
                      onValueChange={(value) => setTcDocUploadedFilter(value as 'all' | 'uploaded' | 'not_uploaded')}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="TC doc uploaded" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">TC Doc: All</SelectItem>
                        <SelectItem value="uploaded">TC Doc: Uploaded</SelectItem>
                        <SelectItem value="not_uploaded">TC Doc: Not Uploaded</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Empty State or Content */}
            {filteredData.length === 0 ? (
              <Card className="border-primary-200 bg-white shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <Search className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-600">No records match your filters</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Try adjusting your search or category filter
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Summary Card */}
                {filteredData.length > 0 && (() => {
              // Calculate current month metrics
              const currentInventoryValue = filteredData.reduce((sum, item) => sum + (item.remainingQuantity || 0), 0);
              const currentInventoryCost = filteredData.reduce((sum, item) => {
                const remainingQty = item.remainingQuantity || 0;
                const unitPrice = item.unitPrice || 0;
                return sum + (remainingQty * unitPrice);
              }, 0);
              
              // Calculate last month metrics (if available)
              const lastMonthInventoryValue = lastMonthData?.inventoryValue || 0;
              const lastMonthInventoryCost = lastMonthData?.inventoryCost || 0;
              
              // Calculate increase/decrease
              const inventoryValueChange = currentInventoryValue - lastMonthInventoryValue;
              const inventoryCostChange = currentInventoryCost - lastMonthInventoryCost;
              
              return (
                <Card className="border-primary-200 bg-gradient-to-r from-primary-50 to-white">
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Inventory Value */}
                      <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                        <div className="text-2xl font-bold text-primary-600 mb-2">
                          {formatNumber(currentInventoryValue)}
                        </div>
                        <div className="text-sm font-semibold text-gray-700 mb-2">Inventory Value</div>
                        {/* <div className="text-xs text-gray-500">(Sum of Remaining Qty)</div> */}
                        {lastMonthData && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs text-gray-600 mb-1">Last Month: {formatNumber(lastMonthInventoryValue)}</div>
                            <div className={`text-sm font-semibold ${inventoryValueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {inventoryValueChange >= 0 ? '+' : ''}{formatNumber(inventoryValueChange)}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Inventory Cost */}
                      <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                        <div className="text-2xl font-bold text-blue-600 mb-2">
                          {formatCurrency(currentInventoryCost)}
                        </div>
                        <div className="text-sm font-semibold text-gray-700 mb-2">Inventory Cost</div>
                        {/* <div className="text-xs text-gray-500">(Remaining Qty × Unit Price)</div> */}
                        {lastMonthData && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="text-xs text-gray-600 mb-1">Last Month: {formatCurrency(lastMonthInventoryCost)}</div>
                            <div className={`text-sm font-semibold ${inventoryCostChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {inventoryCostChange >= 0 ? '+' : ''}{formatCurrency(inventoryCostChange)}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Last Month Status */}
                      <div className="text-center p-4 bg-white rounded-lg border border-gray-200">
                        <div className="text-sm font-semibold text-gray-700 mb-2">Last Month Comparison</div>
                        {loadingLastMonth ? (
                          <div className="text-xs text-gray-500">Loading...</div>
                        ) : lastMonthData ? (
                          <div className="space-y-2">
                            <div className="text-xs text-gray-600">
                              <div>Value: {formatNumber(lastMonthInventoryValue)}</div>
                              <div className="mt-1">Cost: {formatCurrency(lastMonthInventoryCost)}</div>
                            </div>
                            <div className="pt-2 border-t border-gray-200">
                              <div className={`text-sm font-semibold ${inventoryValueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                Value: {inventoryValueChange >= 0 ? '↑' : '↓'} {Math.abs(inventoryValueChange).toFixed(2)}
                              </div>
                              <div className={`text-sm font-semibold mt-1 ${inventoryCostChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                Cost: {inventoryCostChange >= 0 ? '↑' : '↓'} {formatCurrency(Math.abs(inventoryCostChange))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-500">Data not available</div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Table Controls */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {filteredData.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredData.length)} of {filteredData.length} {filteredData.length === 1 ? 'record' : 'records'}
                {reportData.length !== filteredData.length && (
                  <span className="text-gray-400 ml-1">
                    (filtered from {reportData.length} total)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Items per page:</span>
                <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                  <SelectTrigger className="w-20 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paginationConfig.itemsPerPageOptions.map((option) => (
                      <SelectItem key={option} value={option.toString()}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Main Table - Individual Items */}
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Heat No</TableHead>
                      <TableHead className="font-semibold">Item SKU</TableHead>
                      <TableHead className="font-semibold w-48">Item Name</TableHead>
                      <TableHead className="font-semibold">Grade</TableHead>
                      <TableHead className="font-semibold">Supplier</TableHead>
                      <TableHead className="font-semibold">PO Number</TableHead>
                      <TableHead className="font-semibold">GRN Number</TableHead>
                      <TableHead className="font-semibold">GRN Date</TableHead>
                      <TableHead className="font-semibold">Supplier Heat No</TableHead>
                      <TableHead className="font-semibold">TC No</TableHead>
                      <TableHead className="font-semibold">TC Doc Uploaded</TableHead>
                      <TableHead className="font-semibold">DC No</TableHead>
                      <TableHead className="font-semibold text-right">Unit Price</TableHead>
                      <TableHead className="font-semibold text-right">PO Qty</TableHead>
                      <TableHead className="font-semibold text-right">Received Qty</TableHead>
                      <TableHead className="font-semibold text-right">NOP</TableHead>
                      <TableHead className="font-semibold text-right">Total Consumed</TableHead>
                      <TableHead className="font-semibold text-right">Challan Inbound</TableHead>
                      <TableHead className="font-semibold text-right">Challan Outbound</TableHead>
                      <TableHead className="font-semibold text-right">Reject Qty</TableHead>
                      <TableHead className="font-semibold text-right">Remaining Qty</TableHead>
                      <TableHead className="font-semibold text-right">Remaining NOP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((item, index) => (
                      <TableRow key={index} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          {item.heatNo || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {item.rawItemSku || 'N/A'}
                        </TableCell>
                        <TableCell className="w-48 max-w-48">
                          <div className="break-words whitespace-normal">
                            {item.itemName && item.rawItemId ? (
                              <button
                                onClick={() => window.open(`/raw-items/${item.rawItemId}?view=true`, '_blank')}
                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                              >
                                {item.itemName}
                              </button>
                            ) : (
                              item.itemName || 'N/A'
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.grade || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {item.supplierName || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {item.poNumber && item.poId ? (
                            <button
                              type="button"
                              onClick={() => window.open(`/purchase-orders/${item.poId}/pdf`, '_blank')}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                            >
                              {item.poNumber}
                            </button>
                          ) : (
                            item.poNumber || 'N/A'
                          )}
                        </TableCell>
                        <TableCell>
                          {item.grnNumber && item.grnId ? (
                            <button
                              onClick={() => window.open(`/grn/view/${item.grnId}`, '_blank')}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {item.grnNumber}
                            </button>
                          ) : (
                            item.grnNumber || 'N/A'
                          )}
                        </TableCell>
                        <TableCell>
                          {item.grnDate ? (() => {
                            const d = new Date(item.grnDate);
                            if (isNaN(d.getTime())) return 'N/A';
                            const day = String(d.getDate()).padStart(2, '0');
                            const month = String(d.getMonth() + 1).padStart(2, '0');
                            return `${day}-${month}-${d.getFullYear()}`;
                          })() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {item.supplierHeatNo || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {item.tcNo || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {item.tcDocumentUploaded === true ? (
                            <Badge variant="outline" className="border-green-300 text-green-700 bg-green-50">
                              Yes
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
                              No
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.dcNo || 'N/A'}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.unitPrice ? `₹${formatNumber(item.unitPrice)}` : 'N/A'}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(item.poQuantity)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(item.receivedQuantity)}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.nop !== undefined && item.nop !== null ? formatNumber(item.nop) : 'N/A'}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(item.totalConsumed)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(item.totalChallanInbound)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(item.totalChallanOutbound)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(item.rejectQuantity)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={item.remainingQuantity && item.remainingQuantity < 0 ? 'text-red-600 font-semibold' : ''}>
                            {formatNumber(item.remainingQuantity)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {item.remainingNop !== undefined && item.remainingNop !== null ? formatNumber(item.remainingNop) : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}

            {/* Category Summary Table - COMMENTED OUT */}
            {/* {filteredCategoryGroups.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Inventory Summary by Category</h3>
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-primary-50">
                          <TableHead className="font-semibold">Category</TableHead>
                          <TableHead className="font-semibold text-right">PO Qty</TableHead>
                          <TableHead className="font-semibold text-right">Received Qty</TableHead>
                          <TableHead className="font-semibold text-right">Total Consumed</TableHead>
                          <TableHead className="font-semibold text-right">Challan Inbound</TableHead>
                          <TableHead className="font-semibold text-right">Challan Outbound</TableHead>
                          <TableHead className="font-semibold text-right">Reject Qty</TableHead>
                          <TableHead className="font-semibold text-right">Remaining Qty</TableHead>
                          <TableHead className="font-semibold text-center">Item Count</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredCategoryGroups.map((group) => (
                          <TableRow key={group.categoryId} className="hover:bg-gray-50">
                            <TableCell className="font-medium">
                              {group.categoryName}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(group.totals.poQuantity)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(group.totals.receivedQuantity)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(group.totals.totalConsumed)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(group.totals.totalChallanInbound)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(group.totals.totalChallanOutbound)}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(group.totals.rejectQuantity)}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={group.totals.remainingQuantity && group.totals.remainingQuantity < 0 ? 'text-red-600 font-semibold' : ''}>
                                {formatNumber(group.totals.remainingQuantity)}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">
                                {group.items.length}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )} */}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RMInventoryReportPage;

